import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const Withdrawals = () => {
  const { showSuccess, showError } = useNotification();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchWithdrawals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchWithdrawals = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await adminAPI.getWithdrawals(params);
      if (response.data.success) {
        setWithdrawals(response.data.data || []);
      }
    } catch (error) {
      showError('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this withdrawal?')) return;
    try {
      await adminAPI.approveWithdrawal(id, {});
      showSuccess('Withdrawal approved successfully');
      fetchWithdrawals();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to approve withdrawal');
    }
  };

  const handleReject = async (id) => {
    if (!notes.trim()) {
      showError('Please provide a reason for rejection');
      setSelectedWithdrawal(id);
      setShowNotesModal(true);
      return;
    }
    try {
      await adminAPI.rejectWithdrawal(id, { admin_notes: notes });
      showSuccess('Withdrawal rejected successfully');
      setShowNotesModal(false);
      setNotes('');
      setSelectedWithdrawal(null);
      fetchWithdrawals();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to reject withdrawal');
    }
  };

  const openRejectModal = (id) => {
    setSelectedWithdrawal(id);
    setShowNotesModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-600';
      case 'approved':
        return 'bg-green-600';
      case 'rejected':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading withdrawals...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Manage Withdrawals</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'all' ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'pending' ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'approved' ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'rejected' ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
          >
            Rejected
          </button>
        </div>
      </div>

      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4 text-gold">Reject Withdrawal</h2>
            <p className="text-gray-400 mb-4">Please provide a reason for rejection:</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600 mb-4"
              placeholder="Enter rejection reason..."
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (selectedWithdrawal && notes.trim()) {
                    handleReject(selectedWithdrawal);
                  }
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium"
              >
                Reject
              </button>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setNotes('');
                  setSelectedWithdrawal(null);
                }}
                className="bg-gray-700 text-light px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {withdrawals.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No withdrawals found.</p>
          </div>
        ) : (
          withdrawals.map((withdrawal) => (
            <div key={withdrawal._id} className="bg-gray-800 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">
                      {withdrawal.user_id?.name || 'Unknown User'}
                    </h3>
                    <span className={`px-2 py-1 rounded text-xs capitalize ${getStatusColor(withdrawal.status)}`}>
                      {withdrawal.status}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-600 capitalize">
                      {withdrawal.user_role}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-400 mb-2">
                    <div>
                      <p>Requested: <span className="text-gold">₹{withdrawal.requested_amount}</span></p>
                      <p>Final Amount: <span className="text-gold">₹{withdrawal.final_amount}</span></p>
                    </div>
                    <div>
                      {withdrawal.deduction_amount > 0 && (
                        <p>Deduction: <span className="text-red-400">₹{withdrawal.deduction_amount}</span></p>
                      )}
                      <p>Date: {new Date(withdrawal.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="mb-2 p-3 bg-gray-700 rounded text-sm text-gray-300">
                    <p className="font-semibold text-gold mb-1">Bank Details:</p>
                    <p>Bank: {withdrawal.bank_name}</p>
                    <p>IFSC: {withdrawal.ifsc_code}</p>
                    <p>Account: {withdrawal.account_number}</p>
                  </div>
                  {withdrawal.admin_notes && (
                    <div className="mt-2 p-2 bg-gray-700 rounded text-sm">
                      <p className="text-gray-300"><strong>Admin Notes:</strong> {withdrawal.admin_notes}</p>
                    </div>
                  )}
                  {withdrawal.processed_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      Processed on: {new Date(withdrawal.processed_at).toLocaleString()}
                    </p>
                  )}
                </div>
                {withdrawal.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleApprove(withdrawal._id)}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => openRejectModal(withdrawal._id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Withdrawals;
